import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';

import classNames from 'classnames';

import Container from 'reactstrap/lib/Container';

import { ShippingInformation } from '@ttstr/api';
import { loadCart } from '@ttstr/actions';
import { AppState } from '@ttstr/reducers';

import { useIntl, LoadingSpinner } from '@ttstr/components';
import Confirm from '@ttstr/components/Checkout/Confirm';
import Payment from '@ttstr/components/Checkout/Payment';
import Shipping from '@ttstr/components/Checkout/Shipping';
import { useActions, useShallowEqualSelector, useMounted } from '@ttstr/utils';
import { PaymentInformation } from '../../api/cart';

interface RouteParams {
  step?: string;
}

interface OwnProps {
  closeIcon?: React.ReactNode;
}

type Props = Readonly<OwnProps>;

/**
 * If one of these keys is present in the errors object, shipping is not presumed to be "done"
 */
const shippingNotFinishedKeys: Partial<keyof ShippingInformation | string>[] = [
  'address_addition',
  'city',
  'companyname',
  'country',
  'email',
  'firstname',
  'lastname',
  'phone',
  'shipping_address_addition',
  'shipping_city',
  'shipping_companyname',
  'shipping_country',
  'shipping_firstname',
  'shipping_lastname',
  'shipping_method',
  'shipping_method_id',
  'shipping_method_sid',
  'shipping_state',
  'shipping_street',
  'shipping_zip_code',
  'state',
  'street',
  'use_customer_address',
  'use_shipping_address',
];

/**
 * If one of these keys is present in the errors object, payment is not presumed to be "done"
 */
const paymentNotFinishedKeys: Partial<keyof PaymentInformation | string>[] = [
  'payment_method',
  'payment_method_id',
  'payment_method_sid',
  'payment_attributes',
  'payment_method_title',
];

const CheckoutWrapper: React.FC<Props> = ({ closeIcon }) => {
  const { t } = useTranslation();
  const match = useRouteMatch<RouteParams>();
  const isMounted = useMounted();
  const [initialized, setInitialized] = React.useState(false);
  const { cart, cartReceived, loading } = useShallowEqualSelector(mapStateToProps);
  const { loadCart } = useActions(mapDispatchToProps);
  const { language } = useIntl();
  const shippingDone = React.useMemo(() => {
    if (!cartReceived || !cart) return false;
    return !Object.keys(cart.errors).some((key) => shippingNotFinishedKeys.includes(key as any));
  }, [cartReceived, cart]);
  const paymentDone = React.useMemo(() => {
    if (!cartReceived || !cart) return false;
    return !Object.keys(cart.errors).some((key) => paymentNotFinishedKeys.includes(key as any));
  }, [cartReceived, cart]);

  React.useEffect(() => {
    (async () => {
      try {
        await loadCart();
      } finally {
        if (isMounted.current) setInitialized(true);
      }
    })();
  }, [language]);

  function isActivatable(step: string) {
    if (!cart) return false;

    switch (step) {
      case 'shipping':
        return true;
      case 'payment':
        return shippingDone;
      case 'confirm':
        return shippingDone && paymentDone;
      default:
        return false;
    }
  }

  function isPayPal2() {
    return cart.payment_method_sid === 'paypal2';
  }

  function getStepState(step: string) {
    return {
      active: isActive(step),
      activatable: isActivatable(step),
    };
  }

  function isActive(step: string) {
    if (!match.params.step && step === 'shipping') return true;
    if (step === match.params.step) return true;
    return false;
  }

  if (!initialized || (!cart && loading)) {
    return <LoadingSpinner label={t(`LOADING.CART`)} />;
  }

  // CAUTION: Only disable for dev purposes
  if (cartReceived && (!cart || !cart.items || !cart.items.length)) return <Redirect to="/cart/expired" />;

  return (
    <section className="checkout">
      <Container>
        <ol className="list-steps">
          <li className={classNames(getStepState('shipping'))}>
            {isActivatable('shipping') ? (
              <Link to="/checkout">{t('CHECKOUT.SHIPPING_TITLE')}</Link>
            ) : (
              t('CHECKOUT.SHIPPING_TITLE')
            )}
          </li>
          <li className={classNames(getStepState('payment'))}>
            {isActivatable('payment') ? (
              <Link to="/checkout/payment">{t('CHECKOUT.PAYMENT_TITLE')}</Link>
            ) : (
              t('CHECKOUT.PAYMENT_TITLE')
            )}
          </li>
          <li className={classNames(getStepState('confirm'))}>
            {isActivatable('confirm') && !isPayPal2() ? (
              <Link to="/checkout/confirm">{t('CHECKOUT.CONFIRM_TITLE')}</Link>
            ) : (
              t('CHECKOUT.CONFIRM_TITLE')
            )}
          </li>
        </ol>
      </Container>

      <Switch>
        {isActivatable('payment') ? (
          <Route exact path={`${match.path}/payment`} component={Payment} />
        ) : (
          <Redirect exact path={`${match.path}/payment`} to="/checkout" />
        )}

        {isActivatable('confirm') ? (
          <Route exact path={`${match.path}/confirm`} component={Confirm} />
        ) : (
          <Redirect exact path={`${match.path}/confirm`} to="/checkout/payment" />
        )}

        <Route exact>
          <Shipping closeIcon={closeIcon} />
        </Route>
      </Switch>
    </section>
  );
};

const mapStateToProps = (state: AppState) => {
  const { cart, cartReceived, loading } = state.Cart;

  return {
    cart,
    cartReceived,
    shippingMethods: cart?.available_shipping_methods,
    loading,
  };
};

const mapDispatchToProps = {
  loadCart,
};

export default React.memo(CheckoutWrapper);
